// Global
import React, { useId, useRef } from 'react';

// Local
import styles from './VerticalVideoModal.module.scss';
import { YoutubeEmbed } from 'components/helpers/Video/YoutubeVideo';
import { TiktokEmbed } from 'components/helpers/Video/TiktokVideo';
import { InstagramEmbed } from 'components/helpers/Video/InstagramVideo';
import { ModalDialog } from 'components/helpers/ModalDialog';
import OneTrustFilter from 'components/helpers/OneTrust/OneTrustFilter';
import useDictionary from 'lib/hooks/useDictionary';
import useEmbeddedVideo from 'lib/hooks/video/useEmbeddedVideo';
import type { VerticalVideoCardData } from '../VerticalVideoCard/VerticalVideoCard';
import { SvgIcon } from 'components/helpers/SvgIcon';

export interface VerticalVideoModalProps {
  isOpen: boolean;
  video: VerticalVideoCardData | null;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLButtonElement | HTMLElement | null>;
}

export default function VerticalVideoModal({
  isOpen,
  video,
  onClose,
  triggerRef,
}: VerticalVideoModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const { getDictionaryValue } = useDictionary();
  const { extractedVideoId, extractedVideoType } = useEmbeddedVideo(video?.videoUrl || '');

  if (!isOpen || !video) return null;

  const resolvedVideoType =
    extractedVideoType && extractedVideoType !== 'unknown'
      ? extractedVideoType.trim()
      : (video.videoType || '').trim();
  const resolvedVideoId = (extractedVideoId || video.videoId || '').trim();

  const renderPlayer = () => {
    switch (resolvedVideoType) {
      case 'YouTube':
      case 'YouTube Shorts':
        if (!resolvedVideoId) return null;
        return (
          <YoutubeEmbed
            videoId={resolvedVideoId}
            videoTitle={video.videoTitle}
            videoUrl={video.videoUrl || ''}
            autoplay={true}
            isPortrait={resolvedVideoType === 'YouTube Shorts'}
          />
        );
      case 'TikTok':
        if (!resolvedVideoId) return null;
        return <TiktokEmbed videoId={resolvedVideoId} iframeTitle={video.videoTitle} />;
      case 'Instagram':
        if (!resolvedVideoId && !video.videoUrl) return null;
        return (
          <InstagramEmbed
            videoId={resolvedVideoId}
            iframeTitle={video.videoTitle}
            videoUrl={video.videoUrl}
          />
        );
      default:
        return null;
    }
  };

  const modalBodyText = (video.videoDescription || resolvedVideoType || '').trim();

  const closeButton = (
    <button
      ref={closeButtonRef}
      className={styles.modalCloseButton}
      onClick={onClose}
      aria-label={getDictionaryValue('Helpers.VerticalVideo.CloseModal', 'Close video modal')}
    >
      <SvgIcon icon="close" size="xs" aria-hidden="true" />
    </button>
  );

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      ariaLabelledBy={titleId}
      ariaDescribedBy={modalBodyText ? descriptionId : undefined}
      triggerRef={triggerRef}
      initialFocusRef={closeButtonRef as React.RefObject<HTMLElement>}
      headerActions={closeButton}
    >
      <div className={styles.modalLayout}>
        <div className={styles.modalContentRow}>
          <h2 id={titleId} className={styles.modalTitle}>
            {video.videoTitle}
          </h2>
          {modalBodyText && (
            <p id={descriptionId} className={styles.modalSubtitle}>
              {modalBodyText}
            </p>
          )}
        </div>

        <div className={styles.modalMediaRow}>
          <OneTrustFilter
            filteredComponent={<div className={styles.modalEmbedFrame}>{renderPlayer()}</div>}
            placeholderFeatureType="video"
            optanonGroups={['2', '3', '4']}
            enablePlaceholder={true}
          />
        </div>
      </div>
    </ModalDialog>
  );
}
